import * as React from 'react';
import { createContext, useContext, useCallback, useRef } from 'react';

interface CachedIdea {
  data: any;
  timestamp: number;
}

interface IdeaCacheContextType {
  getCachedIdea: (ideaId: string) => any | null;
  setCachedIdea: (ideaId: string, data: any) => void;
  clearCache: (ideaId?: string) => void;
  hasCachedIdea: (ideaId: string) => boolean;
}

const IdeaCacheContext = createContext<IdeaCacheContextType | undefined>(undefined);

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export const IdeaCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Map<string, CachedIdea>>(new Map());

  const getCachedIdea = useCallback((ideaId: string) => {
    console.time(`getCachedIdea-${ideaId}`);
    const cached = cacheRef.current.get(ideaId);
    
    if (!cached) {
      console.timeEnd(`getCachedIdea-${ideaId}`);
      return null;
    }

    const now = Date.now();
    if (now - cached.timestamp > CACHE_DURATION) {
      cacheRef.current.delete(ideaId);
      console.timeEnd(`getCachedIdea-${ideaId}`);
      return null;
    }

    console.log(`[Cache HIT] Retrieved idea ${ideaId} from cache`);
    console.timeEnd(`getCachedIdea-${ideaId}`);
    return cached.data;
  }, []);

  const setCachedIdea = useCallback((ideaId: string, data: any) => {
    console.log(`[Cache SET] Caching idea ${ideaId}`);
    cacheRef.current.set(ideaId, {
      data,
      timestamp: Date.now(),
    });
  }, []);

  const clearCache = useCallback((ideaId?: string) => {
    if (ideaId) {
      console.log(`[Cache CLEAR] Clearing cache for idea ${ideaId}`);
      cacheRef.current.delete(ideaId);
    } else {
      console.log(`[Cache CLEAR] Clearing entire cache`);
      cacheRef.current.clear();
    }
  }, []);

  const hasCachedIdea = useCallback((ideaId: string) => {
    return cacheRef.current.has(ideaId);
  }, []);

  return (
    <IdeaCacheContext.Provider value={{ getCachedIdea, setCachedIdea, clearCache, hasCachedIdea }}>
      {children}
    </IdeaCacheContext.Provider>
  );
};

export const useIdeaCache = () => {
  const context = useContext(IdeaCacheContext);
  if (!context) {
    throw new Error('useIdeaCache must be used within IdeaCacheProvider');
  }
  return context;
};
